import { Component } from '@theme/component';

/**
 * Progressively enhances the existing GET form. Product options always open
 * the original product page so a personalized gift cannot bypass its editor.
 * @extends {Component<{input: HTMLInputElement, form: HTMLFormElement, panel: HTMLElement, list: HTMLUListElement, message: HTMLElement, status: HTMLElement}>}
 */
class NuzzlePredictiveSearch extends Component {
  requiredRefs = ['input', 'form', 'panel', 'list', 'message', 'status'];
  #listeners;
  #request;
  #timer;
  #sequence = 0;
  #active = -1;
  #composing = false;

  connectedCallback() {
    super.connectedCallback();
    this.#bind();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bind();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#cancel();
  }

  #bind() {
    this.#listeners?.abort();
    this.#cancel();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    const { input, form } = this.refs;
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-controls', this.refs.list.id);
    input.setAttribute('aria-haspopup', 'listbox');
    input.setAttribute('aria-expanded', 'false');
    input.addEventListener('input', () => { if (!this.#composing) this.#schedule(); }, options);
    input.addEventListener('focus', () => this.#schedule(), options);
    input.addEventListener('compositionstart', () => { this.#composing = true; this.#close(); }, options);
    input.addEventListener('compositionend', () => { this.#composing = false; this.#schedule(); }, options);
    document.addEventListener('pointerdown', event => {
      if (!this.contains(event.target)) this.#close();
    }, options);
    this.addEventListener('keydown', event => this.#onKeydown(event), options);
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) this.#close();
    }, options);
    // The native submit path is deliberately never prevented.
    form.addEventListener('submit', () => this.#close(), options);
    this.classList.add('nr-search-ready');
    this.#close();
  }

  #cancel() {
    clearTimeout(this.#timer);
    this.#request?.abort();
    this.#request = undefined;
    this.#sequence++;
  }

  #close() {
    this.#cancel();
    this.refs.panel.hidden = true;
    this.refs.panel.removeAttribute('aria-busy');
    this.refs.input.setAttribute('aria-expanded', 'false');
    this.refs.input.removeAttribute('aria-activedescendant');
    this.refs.status.textContent = '';
    this.#active = -1;
  }

  #query() {
    return this.refs.input.value.trim();
  }

  #schedule() {
    this.#cancel();
    const query = this.#query();
    if (query.length < 3 || this.#composing) {
      this.#close();
      return;
    }
    // Clear stale product choices immediately, not after the next response.
    this.#render([], this.dataset.loading);
    this.refs.panel.setAttribute('aria-busy', 'true');
    const sequence = this.#sequence;
    this.#timer = setTimeout(() => this.#search(query, sequence), 250);
  }

  async #search(query, sequence) {
    const controller = new AbortController();
    this.#request = controller;
    const timeout = setTimeout(() => controller.abort(), 6000);
    try {
      const endpoint = new URL(this.dataset.predictiveUrl, location.origin);
      endpoint.search = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '4',
        'resources[options][unavailable_products]': 'hide',
      }).toString();
      const response = await fetch(endpoint, { signal: controller.signal, headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Predictive search unavailable');
      const data = await response.json();
      if (!this.#isCurrent(query, sequence)) return;
      if (!Array.isArray(data?.resources?.results?.products)) throw new Error('Invalid predictive response');
      const products = data.resources.results.products.slice(0, 4);
      this.#render(products, this.dataset.empty);
    } catch {
      // Abort due to a newer query/blur/Escape must never reopen the popup.
      if (!this.#isCurrent(query, sequence)) return;
      this.#render([], this.dataset.unavailable);
    } finally {
      clearTimeout(timeout);
      if (this.#isCurrent(query, sequence)) this.refs.panel.removeAttribute('aria-busy');
      if (this.#request === controller) this.#request = undefined;
    }
  }

  #isCurrent(query, sequence) {
    return this.isConnected && sequence === this.#sequence && query === this.#query() && this.contains(document.activeElement);
  }

  #productUrl(value) {
    try {
      const url = new URL(value, location.origin);
      if (url.origin !== location.origin || !/^(?:\/[^/]+)?\/products\/[^/]+\/?$/.test(url.pathname)) return null;
      return url.pathname + url.search;
    } catch {
      return null;
    }
  }

  #imageUrl(value) {
    if (typeof value !== 'string' || !value.trim()) return null;
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' && (url.origin === location.origin || url.hostname === 'cdn.shopify.com') ? url.href : null;
    } catch {
      return null;
    }
  }

  #option(label, href, index) {
    const item = document.createElement('li');
    item.setAttribute('role', 'presentation');
    const link = document.createElement('a');
    link.id = `${this.refs.list.id}-option-${index}`;
    link.href = href;
    link.className = 'nr-predictive__option';
    link.setAttribute('role', 'option');
    link.setAttribute('aria-selected', 'false');
    link.tabIndex = -1;
    link.textContent = label;
    item.append(link);
    return { item, link };
  }

  #render(products, fallbackMessage) {
    const { list, panel, message, input, status } = this.refs;
    list.replaceChildren();
    this.#active = -1;
    input.removeAttribute('aria-activedescendant');
    const seen = new Set();
    let count = 0;
    for (const product of products) {
      const href = this.#productUrl(product?.url);
      if (!href || product.available === false || typeof product.title !== 'string' || !product.title.trim() || seen.has(href)) continue;
      seen.add(href);
      const { item, link } = this.#option('', href, count++);
      const imageUrl = this.#imageUrl(product.featured_image?.url || product.image);
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = '';
        image.width = 56;
        image.height = 56;
        image.loading = 'lazy';
        link.append(image);
      }
      const title = document.createElement('span');
      title.className = 'nr-predictive__title';
      title.textContent = product.title;
      link.append(title);
      list.append(item);
    }
    message.textContent = count ? this.dataset.results : fallbackMessage;
    const allUrl = new URL(this.refs.form.action, location.origin);
    allUrl.search = new URLSearchParams({ q: this.#query(), type: 'product' }).toString();
    const { item, link } = this.#option(this.dataset.viewAll, allUrl.pathname + allUrl.search, count);
    link.classList.add('nr-predictive__all');
    // The final option also stays in normal Tab order as a fallback.
    link.tabIndex = 0;
    list.append(item);
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    status.textContent = count ? this.dataset.count.replace('__COUNT__', String(count)) : fallbackMessage;
  }

  #onKeydown(event) {
    if (event.isComposing || this.#composing) return;
    if (event.key === 'Escape' && !this.refs.panel.hidden) {
      event.preventDefault();
      event.stopPropagation();
      this.refs.input.focus();
      this.#close();
      return;
    }
    if (event.target !== this.refs.input || this.refs.panel.hidden) return;
    const options = [...this.refs.list.querySelectorAll('[role="option"]')];
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.#active = this.#active < 0 ? (direction > 0 ? 0 : options.length - 1) : (this.#active + direction + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.#active)));
      const selected = options[this.#active];
      this.refs.input.setAttribute('aria-activedescendant', selected.id);
      selected.scrollIntoView({ block: 'nearest', behavior: 'instant' });
    } else if (event.key === 'Enter' && this.#active >= 0) {
      event.preventDefault();
      options[this.#active]?.click();
    }
  }
}

if (!customElements.get('nuzzle-predictive-search')) customElements.define('nuzzle-predictive-search', NuzzlePredictiveSearch);
